'use client';

import type * as React from 'react';
import { Suspense } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { AuthProvider } from '@workspace/lib/auth/auth-provider';
import type { SSOUser } from '@workspace/lib/auth/config';
import { SessionTimeoutDialog } from '@workspace/lib/auth/SessionTimeoutDialog';
import { usePathTracker } from '@workspace/lib/hooks/analytics/use-path-tracker';
import { LoadingProvider } from '@workspace/lib/providers/loading-provider';
import { NotificationProvider } from '@workspace/lib/providers/notification-provider';
import { getRecaptchaSiteKey } from '@workspace/lib/utils/recaptcha-config';
import { NewRelicPageViewTracker } from '@workspace/newrelic-browser/nextjs';

import { useActivePageRestoration } from '@/hooks/use-active-page-restoration';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { useUtmTracking } from '@/hooks/use-utm-tracking';
import { getStepName } from '@/lib/newrelic-step-names';
import { ProgressBarProvider } from '@/providers/progress-bar-provider';

// import { ThemeProvider } from './theme-provider';

function UtmTracker() {
  useUtmTracking();
  return null;
}

function PathTracker() {
  usePathTracker();
  return null;
}

function PageViewTracker() {
  usePageViewTracking();
  return null;
}

interface ProvidersProps {
  initialUser: SSOUser | null;
  children: React.ReactNode;
}

// Read once at module init — stable across renders, no hook needed.
const recaptchaSiteKey = getRecaptchaSiteKey();

export function Providers({ initialUser, children }: ProvidersProps) {
  // TODO: use restoration on the main page when it is ready
  // useActivePageRestoration();

  const content = (
    <>
      <Suspense fallback={null}>
        <PathTracker />
        <UtmTracker />
        <PageViewTracker />
        <NewRelicPageViewTracker inferRouteName={getStepName} />
      </Suspense>
      <NotificationProvider>
        <AuthProvider initialUser={initialUser}>
          <SessionTimeoutDialog />
          <ProgressBarProvider>
            <LoadingProvider>{children}</LoadingProvider>
          </ProgressBarProvider>
        </AuthProvider>
      </NotificationProvider>
    </>
  );

  // Conditionally wrap with GoogleReCaptchaProvider only when enabled AND key is present.
  // When disabled, no external reCAPTCHA script is loaded.
  if (recaptchaSiteKey) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
        {content}
      </GoogleReCaptchaProvider>
    );
  }

  return content;
}
